import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import { useAuth } from '../hooks/useAuth';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

interface PasswordResetConfirmResponse {
  completed: true;
}

interface ResetFieldErrors {
  password: string | null;
  confirmPassword: string | null;
}

function emptyErrors(): ResetFieldErrors {
  return {
    password: null,
    confirmPassword: null,
  };
}

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  usePageMeta({
    title: t('resetPassword.metaTitle'),
    description: t('resetPassword.metaDescription'),
    canonicalPath: '/reset-password',
    robots: 'noindex,nofollow',
  });

  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ResetFieldErrors>(emptyErrors());
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFieldErrors(emptyErrors());
    setFormError(null);

    const nextErrors = emptyErrors();
    let hasError = false;

    if (!token) {
      setFormError(t('resetPassword.invalidToken'));
      return;
    }

    if (!password) {
      nextErrors.password = t('resetPassword.errors.passwordRequired');
      hasError = true;
    } else if (password.length < 8) {
      nextErrors.password = t('resetPassword.errors.passwordLength');
      hasError = true;
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = t('resetPassword.errors.confirmRequired');
      hasError = true;
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = t('resetPassword.errors.confirmMismatch');
      hasError = true;
    }

    if (hasError) {
      setFieldErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post<PasswordResetConfirmResponse>(
        '/auth/password-reset/confirm',
        {
          token,
          password,
        },
        { skipAuth: true },
      );
      setIsComplete(true);
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.statusCode === 400 || error.statusCode === 409) {
          setFormError(t('resetPassword.invalidToken'));
        } else {
          setFormError(error.message || t('resetPassword.errors.unexpected'));
        }
      } else if (error instanceof TypeError) {
        setFormError(t('resetPassword.errors.network'));
      } else {
        setFormError(t('resetPassword.errors.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMissingTokenState = !token && !isComplete;

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('resetPassword.eyebrow')}</div>
            <h1 className="acc-auth-card__title">{t('resetPassword.title')}</h1>
            <p className="acc-auth-card__description">{t('resetPassword.description')}</p>
          </div>

          {formError && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--warning">
              {formError}
            </div>
          )}

          {isComplete ? (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--success">
                {t('resetPassword.success')}
              </div>
              <p className="acc-auth-card__description" style={{ margin: 0 }}>
                {t('resetPassword.successDetail')}
              </p>
              <div className="acc-auth-form__actions">
                <Button type="button" size="lg" style={{ width: '100%' }} onClick={() => navigate('/login', { replace: true })}>
                  {t('resetPassword.backToLogin')}
                </Button>
              </div>
            </div>
          ) : renderMissingTokenState ? (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--warning">
                {t('resetPassword.missingToken')}
              </div>
              <div className="acc-auth-form__actions">
                <Button type="button" size="lg" style={{ width: '100%' }} onClick={() => navigate('/forgot-password', { replace: true })}>
                  {t('resetPassword.requestNewLink')}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="acc-auth-form">
              <div className="acc-auth-form__grid">
                <div className="acc-auth-form__group acc-auth-form__group--span-2">
                  <label htmlFor="reset-password-new" className="acc-auth-form__label">
                    {t('resetPassword.passwordLabel')}
                    <span className="acc-auth-form__meta">{t('resetPassword.passwordHint')}</span>
                  </label>
                  <input
                    id="reset-password-new"
                    type="password"
                    className={`acc-input${fieldErrors.password ? ' acc-input--error' : ''}`}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setFieldErrors((prev) => ({ ...prev, password: null }));
                      setFormError(null);
                    }}
                    autoComplete="new-password"
                    placeholder={t('resetPassword.passwordPlaceholder')}
                    aria-invalid={!!fieldErrors.password}
                    aria-describedby={fieldErrors.password ? 'reset-password-new-error' : undefined}
                  />
                  {fieldErrors.password && (
                    <span id="reset-password-new-error" className="acc-auth-form__error" role="alert">
                      {fieldErrors.password}
                    </span>
                  )}
                </div>

                <div className="acc-auth-form__group acc-auth-form__group--span-2">
                  <label htmlFor="reset-password-confirm" className="acc-auth-form__label">
                    {t('resetPassword.confirmPasswordLabel')}
                  </label>
                  <input
                    id="reset-password-confirm"
                    type="password"
                    className={`acc-input${fieldErrors.confirmPassword ? ' acc-input--error' : ''}`}
                    value={confirmPassword}
                    onChange={(event) => {
                      setConfirmPassword(event.target.value);
                      setFieldErrors((prev) => ({ ...prev, confirmPassword: null }));
                      setFormError(null);
                    }}
                    autoComplete="new-password"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                    aria-invalid={!!fieldErrors.confirmPassword}
                    aria-describedby={fieldErrors.confirmPassword ? 'reset-password-confirm-error' : undefined}
                  />
                  {fieldErrors.confirmPassword && (
                    <span id="reset-password-confirm-error" className="acc-auth-form__error" role="alert">
                      {fieldErrors.confirmPassword}
                    </span>
                  )}
                </div>

                <div className="acc-auth-form__actions acc-auth-form__actions--span-2">
                  <Button type="submit" disabled={isSubmitting} size="lg" style={{ width: '100%' }}>
                    {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
                  </Button>
                </div>
              </div>
            </form>
          )}

          <p className="acc-auth-footer acc-auth-footer--panel">
            <Link
              to={renderMissingTokenState ? '/forgot-password' : '/login'}
              className="acc-link-inline"
            >
              {renderMissingTokenState ? t('resetPassword.requestNewLink') : t('resetPassword.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
